import { v4 } from "uuid";
import { create } from "zustand";
import { SetToggle } from "@/utils/dom";
import type { InstagramSearch } from "../types";
import { useInstagramSearch } from "./useInstagramSearch";

interface InstagramSearchFormState {
  query: string;
  setQuery: (query: string) => void;

  category: InstagramSearch.Option["id"] | undefined;
  setCategory: (category: InstagramSearch.Option["id"] | undefined) => void;

  country: InstagramSearch.Option["id"] | undefined;
  setCountry: (country: InstagramSearch.Option["id"] | undefined) => void;

  regions: SetToggle<InstagramSearch.Option["id"]>;
  toggleRegion: (region: InstagramSearch.Option["id"]) => void;
  setRegions: (regions: InstagramSearch.Option["id"][]) => void;

  cities: SetToggle<InstagramSearch.Option["id"]>;
  toggleCity: (city: InstagramSearch.Option["id"]) => void;
  setCities: (cities: InstagramSearch.Option["id"][]) => void;

  gender: InstagramSearch.Option["id"] | undefined;
  setGender: (gender: InstagramSearch.Option["id"] | undefined) => void;

  followers: InstagramSearch.RangeValue;
  setFollowers: (start: number, end: number) => void;

  posts: InstagramSearch.RangeValue;
  setPosts: (start: number, end: number) => void;

  initial: {
    category: undefined;
    country: undefined;
    regions: SetToggle<InstagramSearch.Option["id"]>;
    cities: SetToggle<InstagramSearch.Option["id"]>;
    gender: undefined;
    followers: InstagramSearch.RangeValue;
    posts: InstagramSearch.RangeValue;
  };
  setInitialState: () => void;
  isStateChanged: () => boolean;

  /**
   * @description Applies the form to the search state. Navigating to the
   * profiles route is up to the caller, which lives inside the router.
   */
  setFormConfirmed: () => void;

  isInitiallySubmitted: boolean;
  setIsInitiallySubmitted: (isInitiallySubmitted: boolean) => void;
}

export const useInstagramSearchForm = create<InstagramSearchFormState>(
  (set, get) => ({
    query: "",
    setQuery: (query) => set({ query }),

    category: undefined,
    setCategory: (category) => set({ category }),

    country: undefined,
    setCountry: (country) => {
      set((state) => {
        return {
          country,
          regions: country === undefined ? new SetToggle() : state.regions,
          cities: country === undefined ? new SetToggle() : state.cities,
        };
      });
    },

    regions: new SetToggle(),
    toggleRegion: (region) => {
      set((state) => {
        const regions = new SetToggle(state.regions);

        regions.toggle(region);

        return {
          regions,
        };
      });
    },
    setRegions: (regions) => {
      set({
        regions: new SetToggle(regions),
      });
    },

    cities: new SetToggle(),
    toggleCity: (city) => {
      set((state) => {
        const cities = new SetToggle(state.cities);

        cities.toggle(city);

        return {
          cities,
        };
      });
    },
    setCities: (cities) => {
      set({
        cities: new SetToggle(cities),
      });
    },

    gender: undefined,
    setGender: (gender) => set({ gender }),

    followers: {
      start: 0,
      end: 0,
    },
    setFollowers: (start, end) => set({ followers: { start, end } }),

    posts: {
      start: 0,
      end: 0,
    },
    setPosts: (start, end) => set({ posts: { start, end } }),

    initial: {
      category: undefined,
      country: undefined,
      regions: new SetToggle(),
      cities: new SetToggle(),
      gender: undefined,
      followers: {
        start: 0,
        end: 0,
      },
      posts: {
        start: 0,
        end: 0,
      },
    },
    setInitialState: () => set((state) => ({ ...state.initial })),

    isStateChanged: () => {
      const state = get();

      return (
        state.category !== state.initial.category ||
        state.country !== state.initial.country ||
        state.regions.difference(state.initial.regions).size > 0 ||
        state.cities.difference(state.initial.cities).size > 0 ||
        state.gender !== state.initial.gender ||
        state.followers.start !== state.initial.followers.start ||
        state.followers.end !== state.initial.followers.end ||
        state.posts.start !== state.initial.posts.start ||
        state.posts.end !== state.initial.posts.end
      );
    },

    setFormConfirmed: () => {
      const state = get();

      useInstagramSearch.setState({
        id: v4(),
        selection: new Set(),
        listsSelection: new Set(),
        query: state.query,
        category: state.category,
        country: state.country,
        regions: state.regions,
        cities: state.cities,
        gender: state.gender,
        followers: state.followers,
        posts: state.posts,
      });
    },

    isInitiallySubmitted: false,
    setIsInitiallySubmitted: (isInitiallySubmitted) => {
      set({
        isInitiallySubmitted,
      });
    },
  }),
);
